import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { mockFeaturedHotels } from "../data/mockData";

const FeaturedHotels = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Extended hotel data for carousel (exactly 8 slides) - Using local images
  const baseHotels = [
    {
      name: "The Ritz-Carlton New York, Central Park",
      category: "PREMIERE PARK VIEW EXPERIENCE",
      image: "/images/1-premiere-park-view-suite-living-room_Classic-Hor.jpeg",
      description: "An unrivaled location in Manhattan's most esteemed neighborhood with breathtaking Central Park views."
    },
    {
      name: "The Ritz-Carlton, Sarasota",
      category: "POOLSIDE LUXURY EXPERIENCE",
      image: "/images/2-sbarz-pool-at-sunset-23048_Classic-Hor.jpeg",
      description: "Experience stunning sunset views from our infinity pool overlooking Sarasota Bay."
    },
    {
      name: "The Ritz-Carlton, Naples",
      category: "RESORT EXTERIOR EXPERIENCE",
      image: "/images/3-tfsrz-resort-exterior.jpeg",
      description: "Discover elegant architecture and pristine beachfront luxury on Florida's Gulf Coast."
    },
    {
      name: "The Ritz-Carlton, Millenia Singapore",
      category: "TRANQUILITY EXPERIENCE",
      image: "/images/4-mlera-finding-tranquility.jpeg",
      description: "Find peace and serenity in our world-class spa and wellness facilities."
    },
    {
      name: "The Ritz-Carlton, Half Moon Bay",
      category: "COASTAL SHORELINE EXPERIENCE",
      image: "/images/5-hafrz-shoreline.jpeg",
      description: "Perched on dramatic bluffs overlooking the Pacific Ocean's rugged coastline."
    },
    {
      name: "The Ritz-Carlton, Cancun",
      category: "MEXICAN RIVIERA EXPERIENCE",
      image: "/images/6-mexrz-ritz-carlton.jpeg",
      description: "Experience luxury on the pristine beaches of Mexico's Caribbean coast."
    },
    {
      name: "The Ritz-Carlton, Melbourne",
      category: "INFINITY POOL EXPERIENCE",
      image: "/images/7-melrz-pool.jpeg",
      description: "Enjoy spectacular city views from our rooftop infinity pool and spa."
    },
    {
      name: "The Ritz-Carlton Executive Collection",
      category: "EXECUTIVE LEVEL EXPERIENCE",
      image: "/images/8-executive.jpg",
      description: "Exclusive access to personalized service and premium amenities on our Executive Level."
    }
  ];

  const [itemsPerView, setItemsPerView] = useState(4);

  useEffect(() => {
    const updateItemsPerView = () => {
      if (window.innerWidth >= 1280) setItemsPerView(4);
      else if (window.innerWidth >= 1024) setItemsPerView(3);
      else if (window.innerWidth >= 768) setItemsPerView(2);
      else setItemsPerView(1);
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % baseHotels.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + baseHotels.length) % baseHotels.length);
  };

  // Auto-scroll for infinite loop effect
  useEffect(() => {
    const interval = setInterval(nextSlide, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-20" style={{ backgroundColor: 'rgb(233, 241, 250)' }}>
      <div className="max-w-7xl mx-auto px-4 mb-16">
        
        {/* Two-Column Header Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column - Big Two-Line Text */}
          <div>
            <h2 className="text-gray-900 leading-tight">
              <span 
                style={{
                  fontFamily: 'Caslon540Std, serif',
                  fontSize: '40px',
                  fontStyle: 'italic',
                  fontWeight: '500',
                  height: '44px',
                  flexBasis: '83.3333%',
                  display: 'block'
                }}
              >
                Where to Go Next
              </span>
              <span 
                style={{
                  fontFamily: 'Caslon540Std, serif',
                  fontSize: '3.75rem', // --t-display-s-font-size-viewport-l
                  lineHeight: '4.125rem', // --t-display-s-line-height-viewport-l
                  fontWeight: '400', // --t-display-s-font-weight
                  letterSpacing: '0', // --t-display-s-letter-space
                  fontStyle: 'normal', // --t-display-s-font-style
                  textTransform: 'uppercase', // --t-display-s-text-transform
                  color: '#1c1c1c', // var(--rzp-50) / --t-brand-fg-color
                  display: 'block'
                }}
              >
                Featured Hotels
              </span>
            </h2>
          </div>

          {/* Right Column - Paragraph and Button */}
          <div className="space-y-6">
            <p className="text-lg text-gray-600 leading-relaxed">
              Boundary-pushing architecture, innovative amenities, intriguing destinations — all with legendary Ritz-Carlton service and attention to detail. Discover hotels and resorts from The Ritz-Carlton and open up a world of possibility.
            </p>
            
            <Button
              className="bg-black text-white hover:bg-gray-800 px-8 py-3 text-sm font-medium transition-colors duration-200 rounded-none"
              style={{ 
                fontFamily: 'Swiss-721, Arial, sans-serif',
                fontSize: '0.8125rem',
                fontWeight: 700,
                letterSpacing: '0.5px'
              }}
            >
              See the Collection
            </Button>
          </div>
        </div>
      </div>

      {/* Custom Carousel - Full Width */}
      <div className="relative">
        {/* Carousel Container */}
        <div className="overflow-hidden">
          <div 
            className="flex transition-transform duration-700 ease-in-out"
            style={{ 
              transform: `translateX(-${(currentIndex * 100) / itemsPerView}%)`,
            }}
          >
            {/* Repeat the hotels array multiple times for infinite effect */}
            {[...baseHotels, ...baseHotels, ...baseHotels].map((hotel, index) => (
              <div 
                key={`hotel-${index}`} 
                className="flex-shrink-0 flex justify-center"
                style={{ width: `${100 / itemsPerView}%` }}
              >
                <div className="relative overflow-hidden group cursor-pointer" style={{ width: '80%' }}>
                  <img
                    src={hotel.image}
                    alt={hotel.name}
                    className="w-full h-[433px] object-cover transition-transform duration-500 group-hover:scale-105"
                    style={{ aspectRatio: '649/433' }}
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <span className="inline-block text-[10px] tracking-[1.2px] mb-2 bg-white/90 text-black px-2 py-1 rounded-sm font-medium">
                      {hotel.category}
                    </span>
                    <h3 className="text-sm font-semibold leading-tight drop-shadow-lg">
                      {hotel.name}
                    </h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Arrows */}
        <button 
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg transition-all duration-200 z-10"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <button 
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg transition-all duration-200 z-10"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Dots Indicator */}
        <div className="flex justify-center mt-6 space-x-2">
          {baseHotels.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                currentIndex === index ? 'bg-gray-800' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedHotels;